import React, { Dispatch, FC, SetStateAction, useContext, useEffect } from 'react';

import { Grid, Paper } from '@material-ui/core';
import moment from 'moment';

import { GlobalContext } from '../../store';
import { ICourse } from '../../store/admin/courses/store';
import { IEventRound } from '../../store/admin/eventRounds/store';
import { IMetaFormat } from '../../store/admin/roundFormats/store';

import { holesOptions } from '../../contrib/utils';
import { SelectHolesForCTP } from '../../scenes/admin/EventWizard/EventFormats/Components/SelectHolesForCTP/SelectHolesForCTP';
import AutoComplete from '../AutoComplete';
import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import Tooltip from '../Tooltip';
import HandicapAllowance from './HandicapAdjustment/HandicapAllowance';
import { useGetAdminEvent } from '../../contrib/Hooks/useGetAdminEvent';
import { useRouteInfoState } from '../RouteInfo/Context/RouteInfo.context';

interface IProps {
  ctpFormatToUpdate?: IMetaFormat;
  displayHandicapAllowance: boolean;
  roundId: number;
  setCTPFormatToUpdate?: Dispatch<SetStateAction<IMetaFormat | undefined>>;
  setValue: (name: string) => (event: any) => void;
  values: IEventRound;
}

const GeneralRoundInfo: FC<IProps> = props => {
  const {
    ctpFormatToUpdate,
    displayHandicapAllowance,
    roundId,
    setCTPFormatToUpdate,
    setValue,
    values
  } = props;

  const {
    actions: {
      adminCourses: { all: getAdminCourses }
    },
    adminCourses: courses
  } = useContext(GlobalContext);

  const { eventId } = useRouteInfoState();
  const event = useGetAdminEvent(eventId);

  const setCourse = async (value: any): Promise<void> => {
    const courseId = Number(value.value);
    setValue('course')(courses.find(c => c.id === courseId));
  };

  const setHoles = (value: any): void => props.setValue('holes')(value.value);
  const onChangeHandicapAdjustment = (value: number): void =>
    props.setValue('handicapAllowance')(value);

  useEffect(() => {
    getAdminCourses();
  }, [getAdminCourses]);

  useEffect(() => {
    if (event) {
      setValue('start')(event.start);
    }
  }, [event]);

  return (
    <Paper elevation={1} className="general-round-info override">
      <Grid container={true} spacing={8}>
        <Grid
          className="general-round-info-section inner-grid-item override"
          item={true}
          xs={12}
          md={6}
        >
          <div className="title__container override">
            <h3 className="title-tooltip override">Date</h3>

            <Tooltip
              classes={{ fab: 'tooltip-fab--shift-top-2dot5' }}
              sectionKey="ROUND_BASICS.DATE"
            />
          </div>

          <DatePicker
            disabled={true}
            id="date-start"
            label="DATE OF ROUND"
            maxDate={moment()
              .add(5, 'years')
              .format('YYYY-MM-DD')}
            onChange={setValue('start')}
            onError={setValue('start')}
            placeholder="10/10/2018"
            value={(event || values).start}
          />
        </Grid>

        <Grid
          className="general-round-info-section inner-grid-item override"
          item={true}
          xs={12}
          md={6}
        >
          <div>
            <div className="title__container override">
              <h3 className="title-tooltip override">Start time</h3>

              <Tooltip
                classes={{ fab: 'tooltip-fab--shift-top-2dot5' }}
                sectionKey="ROUND_BASICS.START_TIME"
              />
            </div>

            <TimePicker label="START TIME" value={values.start} onChange={setValue('start')} />
          </div>
        </Grid>

        <Grid
          className="general-round-info-section general-round-info-section--wide override"
          item={true}
          xs={12}
        >
          <h3 className="general-round-info-section__title general-round-info-section__title--wide override">
            Course details
          </h3>

          <Grid container={true} spacing={8}>
            <Grid
              className="general-round-info-autocomplete inner-grid-item override"
              item={true}
              xs={12}
              md={6}
            >
              <div className="subtitle-tooltip__container override">
                <span className="general-round-info-autocomplete__label">
                  What course are you playing?
                </span>
                <Tooltip
                  classes={{ fab: 'tooltip-fab--shift-top-2' }}
                  sectionKey="ROUND_BASICS.COURSE"
                />
              </div>
              <AutoComplete
                value={
                  values.course &&
                  (courses || [])
                    .map((item: ICourse) => ({
                      label: `${item.name}, ${item.address}`,
                      value: String(item.id)
                    }))
                    .find(c => c.value === String(values.course.id))
                }
                onChange={setCourse}
                options={(courses || []).map((item: ICourse) => ({
                  label: `${item.name}, ${item.address}`,
                  value: String(item.id)
                }))}
              />
            </Grid>
            <Grid
              className="general-round-info-autocomplete inner-grid-item override"
              item={true}
              xs={12}
              md={6}
            >
              <div className="subtitle-tooltip__container override">
                <span className="general-round-info-autocomplete__label">
                  How many holes for this round?
                </span>
                <Tooltip
                  classes={{ fab: 'tooltip-fab--shift-top-2' }}
                  sectionKey="ROUND_BASICS.HOLES"
                />
              </div>
              <AutoComplete
                className="select-holes"
                onChange={setHoles}
                options={holesOptions}
                value={holesOptions.find(h => h.value === String(values.holes))}
              />
            </Grid>

            {ctpFormatToUpdate && ctpFormatToUpdate.rounds.includes(roundId) && (
              <Grid
                className="general-round-info-section general-round-info-section--wide override"
                item={true}
                xs={12}
              >
                <h3 className="general-round-info-section__title general-round-info-section__title--wide override">
                  Contests
                </h3>

                {setCTPFormatToUpdate && (
                  <SelectHolesForCTP
                    metaFormatToUpdate={ctpFormatToUpdate}
                    setMetaFormatToUpdate={setCTPFormatToUpdate}
                    roundId={roundId}
                  />
                )}
              </Grid>
            )}

            <Grid
              className="general-round-info-section general-round-info-section--wide override"
              item={true}
              xs={12}
            >
              {displayHandicapAllowance && (
                <HandicapAllowance
                  defaultValue={values.handicapAllowance}
                  onChange={onChangeHandicapAdjustment}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export { GeneralRoundInfo };
